import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import { getContactId, getCustomerDisplayName, extractCustomerDetails, extractClientDetails } from '../utils/contactHelpers';
import DocumentModalShell from './DocumentModalShell';
import { getBillToFromInvoice, numberToWords, parseCompanyProfile } from '../utils/documentModalHelpers';
import { FileText, Search, AlertCircle, CheckCircle, Eye, Edit, Trash2, Plus, X, Calendar } from 'lucide-react';

const BillsOfSupply = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [ordering, setOrdering] = useState('-invoice_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);
  const [resolvedDetailsCustomer, setResolvedDetailsCustomer] = useState(null);
  const [resolvedDetailsClient, setResolvedDetailsClient] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [editForm, setEditForm] = useState({
    status: 'draft',
    customer: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    subtotal: '',
    tax_amount: '',
    total_amount: '',
    notes: '',
    terms: '',
    customer_name: '',
    customer_company: '',
    customer_address: '',
    customer_gstin: '',
    customer_pan: '',
    items: []
  });
  const [editContactType, setEditContactType] = useState('');
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editCustomers, setEditCustomers] = useState([]);
  const [editClients, setEditClients] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const handleOpenDelete = (bill) => {
    setDeleteTarget(bill);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(
        API_ENDPOINTS.BILLS_OF_SUPPLY_DELETE(deleteTarget.id),
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Bill of Supply deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchBills(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete bill of supply'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const fetchBills = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-invoice_date');

      const response = await axios.get(
        `${API_ENDPOINTS.BILLS_OF_SUPPLY_LIST}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const data = response.data;
      const normalized = Array.isArray(data) ? data : (data.results || []);
      const normalizedCount = Array.isArray(data) ? data.length : (data.count || normalized.length || 0);

      setItems(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch bills of supply'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBills(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, ordering]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchBills(1);
    }, 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (isDetailsOpen || isEditOpen) {
      axios.get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers: getAuthHeaders() })
        .then((res) => setCompanyProfile(parseCompanyProfile(res.data)))
        .catch(() => setCompanyProfile(null));
    }
  }, [isDetailsOpen, isEditOpen]);

  useEffect(() => {
    if (!isDetailsOpen || !selectedBill) {
      setResolvedDetailsCustomer(null);
      setResolvedDetailsClient(null);
      return;
    }

    let cancelled = false;
    const headers = getAuthHeaders();
    const inv = selectedBill;
    const rawCustomer = inv.customer;
    const rawClient = inv.client;
    const customerId =
      rawCustomer != null && typeof rawCustomer !== 'object' ? rawCustomer : null;
    const clientId =
      rawClient != null && typeof rawClient !== 'object' ? rawClient : null;

    const fetchContacts = async () => {
      let cust = null;
      let cli = null;
      if (customerId != null && customerId !== '') {
        try {
          const r = await axios.get(API_ENDPOINTS.CUSTOMERS_DETAIL(customerId), { headers });
          if (!cancelled) cust = r.data;
        } catch {
          /* keep flat / nested fields from bill */
        }
      }
      if (clientId != null && clientId !== '') {
        try {
          const r = await axios.get(API_ENDPOINTS.CLIENTS_DETAIL(clientId), { headers });
          if (!cancelled) cli = r.data;
        } catch {
          /* keep flat fields */
        }
      }
      if (!cancelled) {
        setResolvedDetailsCustomer(cust);
        setResolvedDetailsClient(cli);
      }
    };

    fetchContacts();
    return () => {
      cancelled = true;
    };
  }, [isDetailsOpen, selectedBill?.id, selectedBill?.customer, selectedBill?.client]);

  const detailsBillTo = useMemo(
    () =>
      selectedBill
        ? getBillToFromInvoice(selectedBill, resolvedDetailsCustomer, resolvedDetailsClient)
        : { name: '', company: '', address: '', gstin: '', pan: '' },
    [selectedBill, resolvedDetailsCustomer, resolvedDetailsClient]
  );

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.CUSTOMERS_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || []);
      setEditCustomers(normalized);
      return normalized;
    } catch (err) {
      return [];
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || data.clients || []);
      setEditClients(normalized);
      return normalized;
    } catch (err) {
      return [];
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleView = async (bill) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        API_ENDPOINTS.BILLS_OF_SUPPLY_DETAIL(bill.id),
        { headers: getAuthHeaders() }
      );
      setSelectedBill(response.data);
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch bill of supply details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (bill) => {
    try {
      setIsEditLoading(true);
      const [custList, clientList, detailResponse] = await Promise.all([
        fetchCustomers(),
        fetchClients(),
        axios.get(API_ENDPOINTS.BILLS_OF_SUPPLY_DETAIL(bill.id), { headers: getAuthHeaders() })
      ]);
      const data = detailResponse.data || bill;
      setEditBill(data);
      const contactTypeInit = data.customer ? 'customer' : (data.client ? 'client' : '');
      setEditContactType(contactTypeInit);
      const customerId = getContactId(data.customer) || getContactId(data.client);
      let customerDetails = { name: '', company: '', address: '', gstin: '', pan: '' };
      if (data.customer_name || data.customer_company || data.customer_address) {
        customerDetails = {
          name: data.customer_name || data.client_name || '',
          company: data.customer_company || data.client_company || '',
          address: data.customer_address || data.client_address || '',
          gstin: data.customer_gstin || data.client_gstin || '',
          pan: data.customer_pan || data.client_pan || ''
        };
      } else if (customerId && contactTypeInit === 'customer') {
        const c = custList.find((x) => String(x.id) === customerId);
        if (c) customerDetails = extractCustomerDetails(c);
      } else if (customerId && contactTypeInit === 'client') {
        const c = clientList.find((x) => String(x.id) === customerId);
        if (c) customerDetails = extractClientDetails(c);
      }
      setEditForm({
        status: data.status || 'draft',
        customer: customerId,
        invoice_number: data.invoice_number || '',
        invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
        due_date: data.due_date ? data.due_date.split('T')[0] : (data.invoice_date ? data.invoice_date.split('T')[0] : ''),
        subtotal: data.subtotal || '',
        tax_amount: data.tax_amount || '',
        total_amount: data.total_amount || '',
        notes: data.notes || '',
        terms: data.terms || '',
        customer_name: customerDetails.name,
        customer_company: customerDetails.company,
        customer_address: customerDetails.address,
        customer_gstin: customerDetails.gstin,
        customer_pan: customerDetails.pan,
        items: (data.items || []).map((it, idx) => ({
          id: it.id || idx + 1,
          sac_code: it.sac_code || '',
          hsn_code: it.hsn_code || '',
          description: it.description || '',
          quantity: it.quantity || '',
          rate: it.rate || '',
          amount: it.amount || '',
          tax_rate: it.tax_rate || '',
          tax_amount: it.tax_amount || ''
        }))
      });
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load bill of supply for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editBill?.id) return;
    if (!editContactType || !editForm.customer) {
      setError('Please select a customer or client before updating.');
      return;
    }
    const fallbackInvoiceDate =
      editForm.invoice_date || (editBill?.invoice_date ? editBill.invoice_date.split('T')[0] : '');
    const fallbackDueDate =
      editForm.due_date ||
      (editBill?.due_date ? editBill.due_date.split('T')[0] : '') ||
      fallbackInvoiceDate;
    if (!fallbackInvoiceDate) {
      setError('Invoice date is required.');
      return;
    }
    if (!fallbackDueDate) {
      setError('Due date is required.');
      return;
    }
    const subtotalVal = editForm.subtotal || editBill?.subtotal || '0';
    const taxVal = editForm.tax_amount || editBill?.tax_amount || '0';
    const totalVal = editForm.total_amount || editBill?.total_amount || '0';
    setIsEditLoading(true);
    try {
      const safeItems = Array.isArray(editForm.items) ? editForm.items : [];
      const payload = {
        status: editForm.status,
        invoice_number: editForm.invoice_number || null,
        invoice_date: fallbackInvoiceDate,
        due_date: fallbackDueDate,
        subtotal: subtotalVal?.toString() || '0',
        tax_amount: taxVal?.toString() || '0',
        total_amount: totalVal?.toString() || '0',
        notes: editForm.notes,
        terms: editForm.terms || '',
        customer: editContactType === 'customer' && editForm.customer ? parseInt(editForm.customer) : null,
        client: editContactType === 'client' && editForm.customer ? parseInt(editForm.customer) : null,
        items: safeItems.map((it) => {
          const qty = parseFloat(String(it.quantity ?? '').replace(/,/g, '')) || 1;
          const amt = parseFloat(String(it.amount ?? '').replace(/,/g, '')) || 0;
          const derivedRate = qty > 0 ? (amt / qty).toFixed(2) : String(amt);
          return {
            sac_code: it.sac_code,
            hsn_code: it.hsn_code,
            description: it.description,
            quantity: it.quantity,
            rate: it.rate || derivedRate,
            amount: it.amount,
            tax_rate: it.tax_rate ?? '',
            tax_amount: it.tax_amount ?? ''
          };
        })
      };
      const response = await axios.put(
        API_ENDPOINTS.BILLS_OF_SUPPLY_DETAIL(editBill.id),
        payload,
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Bill of Supply updated successfully';
      setError('');
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setEditBill(null);
      // refresh list after brief delay so toast remains visible
      setTimeout(() => {
        fetchBills(currentPage);
        setSuccessMessage('');
      }, 1200);
    } catch (err) {
      console.error('BOS update error', err?.response?.data || err);
      setError(getErrorMessage(err, 'Failed to update bill of supply'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleEditItemChange = (idx, field, value) => {
    setEditForm((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const handleAddEditItem = () => {
    setEditForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: prev.items.length + 1,
          sac_code: '',
          hsn_code: '',
          description: '',
          quantity: '',
          rate: '',
          amount: '',
          tax_rate: '',
          tax_amount: ''
        }
      ]
    }));
  };

  const handleRemoveEditItem = (idx) => {
    setEditForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Bills of Supply</p>
              <p className="text-2xl font-bold text-foreground">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search bills of supply..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-invoice_date">Date: Newest first</option>
            <option value="invoice_date">Date: Oldest first</option>
            <option value="-total_amount">Amount: High to Low</option>
            <option value="total_amount">Amount: Low to High</option>
          </select>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading bills of supply...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No bills of supply found</p>
            </div>
          ) : (
            <>
              <table className="w-full min-w-[960px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer / Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((bos) => (
                    <tr key={bos.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {bos.invoice_number || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {bos.customer_name || bos.client_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₹{parseFloat(bos.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {bos.invoice_date ? new Date(bos.invoice_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bos.status)}`}>
                          {bos.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(bos)}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(bos)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete"
                            onClick={() => handleOpenDelete(bos)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {Math.ceil(totalCount / 10)}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchBills(currentPage - 1)}
                      disabled={!prevPage}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchBills(currentPage + 1)}
                      disabled={!nextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal - Same design as creation form */}
      {isDetailsOpen && selectedBill && (
        <DocumentModalShell title="Bill of Supply Details" onClose={() => { setIsDetailsOpen(false); setSelectedBill(null); }} banner="BILL OF SUPPLY" companyProfile={companyProfile}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div><p className="text-sm font-medium text-gray-700 mb-1">Bill No</p><p className="font-semibold">{selectedBill.invoice_number || 'N/A'}</p></div>
              <div><p className="text-sm font-medium text-gray-700 mb-1">Bill Date</p><p className="font-semibold">{selectedBill.invoice_date ? new Date(selectedBill.invoice_date).toLocaleDateString() : 'N/A'}</p></div>
              <div><p className="text-sm font-medium text-gray-700 mb-1">Due Date</p><p className="font-semibold">{selectedBill.due_date ? new Date(selectedBill.due_date).toLocaleDateString() : 'N/A'}</p></div>
              <div><p className="text-sm font-medium text-gray-700 mb-1">Status</p><p className="font-semibold capitalize">{selectedBill.status || 'N/A'}</p></div>
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Bill To</h3>
              <p className="text-sm font-medium text-gray-700 mb-1">Name</p>
              <p className="font-semibold">{detailsBillTo.name || 'N/A'}</p>
              <p className="text-sm font-medium text-gray-700 mb-1">Company</p>
              <p className="font-semibold">{detailsBillTo.company || 'N/A'}</p>
              <p className="text-sm font-medium text-gray-700 mb-1">Address</p>
              <p className="font-semibold">{detailsBillTo.address || 'N/A'}</p>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm font-medium text-gray-700 mb-1">GSTIN</p><p className="font-semibold">{detailsBillTo.gstin || 'N/A'}</p></div>
                <div><p className="text-sm font-medium text-gray-700 mb-1">PAN</p><p className="font-semibold">{detailsBillTo.pan || 'N/A'}</p></div>
              </div>
            </div>
          </div>

          {/* Services — same order as TaxInvoiceForm: after Bill To, before amount summary */}
          {selectedBill.items && selectedBill.items.length > 0 && (
            <div className="mt-6">
              <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Services</h3>
              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <table className="min-w-full w-full border-collapse border border-gray-300">
                  <thead><tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left text-xs font-medium">S.No</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-xs font-medium">SAC / HSN Code</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-xs font-medium">Description of Goods / Services</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-xs font-medium">Quantity</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-xs font-medium">Amount (₹)</th>
                  </tr></thead>
                  <tbody>
                    {selectedBill.items.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td className="border border-gray-300 px-4 py-2 text-sm">{idx + 1}</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm">{item.sac_code || item.hsn_code || '—'}</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm">{item.description || '—'}</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm">{item.quantity ?? '—'}</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm">₹{parseFloat(item.amount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-sm text-gray-700">Total Taxable Value:</span><span className="font-semibold">₹{parseFloat(selectedBill.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-700">Tax Amount:</span><span className="font-semibold">₹{parseFloat(selectedBill.tax_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between pt-2 border-t border-gray-300"><span className="font-semibold">Total Invoice Value:</span><span className="font-semibold">₹{parseFloat(selectedBill.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                <p className="text-xs text-gray-600 pt-2">Total (in words): {numberToWords(Math.floor(parseFloat(selectedBill.total_amount || 0)))} Rupees Only</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Bank Details</h3>
              <div className="space-y-2 text-sm">
                <div><span className="text-gray-600">Account Name:</span> <span>{companyProfile?.accountName || '—'}</span></div>
                <div><span className="text-gray-600">Bank Name:</span> <span>{companyProfile?.bankName || '—'}</span></div>
                <div><span className="text-gray-600">IFSC:</span> <span>{companyProfile?.ifscCode || '—'}</span></div>
              </div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Signature</h3>
              <p className="text-sm text-gray-600">For {companyProfile?.name || 'COMPANY NAME'}</p>
              <div className="mt-8 border-t border-gray-300 pt-2"><p className="text-sm">Authorized Signatory</p></div>
            </div>
          </div>
          {(selectedBill.notes || selectedBill.terms) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedBill.notes && <div><label className="block text-sm font-medium text-gray-700 mb-2">Notes</label><p className="text-sm text-gray-600 border border-gray-200 rounded-md p-3 bg-gray-50">{selectedBill.notes}</p></div>}
              {selectedBill.terms && <div><label className="block text-sm font-medium text-gray-700 mb-2">Terms</label><p className="text-sm text-gray-600 border border-gray-200 rounded-md p-3 bg-gray-50">{selectedBill.terms}</p></div>}
            </div>
          )}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelectedBill(null); }}>Close</Button>
          </div>
        </DocumentModalShell>
      )}

      {/* Edit Modal - Same design as creation form */}
      {isEditOpen && editBill && (
        <DocumentModalShell title="Update Bill of Supply" onClose={() => { setIsEditOpen(false); setEditBill(null); }} banner="BILL OF SUPPLY" companyProfile={companyProfile}>
          <form onSubmit={handleSubmitEdit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                  <select
                    value={editContactType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditContactType(v);
                      setEditForm((prev) => ({ ...prev, customer: '', customer_name: '', customer_company: '', customer_address: '', customer_gstin: '', customer_pan: '' }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select type</option>
                    <option value="customer">Customer</option>
                    <option value="client">Client</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editContactType === 'client' ? 'Client' : 'Customer'}
                  </label>
                  <select
                    value={editForm.customer}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && editContactType === 'customer') {
                        const c = editCustomers.find((x) => String(x.id) === val);
                        if (c) {
                          const d = extractCustomerDetails(c);
                          setEditForm((prev) => ({ ...prev, customer: val, customer_name: d.name, customer_company: d.company, customer_address: d.address, customer_gstin: d.gstin, customer_pan: d.pan }));
                        } else setEditForm((prev) => ({ ...prev, customer: val }));
                      } else if (val && editContactType === 'client') {
                        const c = editClients.find((x) => String(x.id) === val);
                        if (c) {
                          const d = extractClientDetails(c);
                          setEditForm((prev) => ({ ...prev, customer: val, customer_name: d.name, customer_company: d.company, customer_address: d.address, customer_gstin: d.gstin, customer_pan: d.pan }));
                        } else setEditForm((prev) => ({ ...prev, customer: val }));
                      } else setEditForm((prev) => ({ ...prev, customer: val, customer_name: '', customer_company: '', customer_address: '', customer_gstin: '', customer_pan: '' }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={!editContactType}
                  >
                    <option value="">Select</option>
                    {editContactType === 'client'
                      ? editClients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.client_name || c.company_name || `Client ${c.id}`}
                          </option>
                        ))
                      : editCustomers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {getCustomerDisplayName(c)}
                          </option>
                        ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={editForm.customer_name} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Customer/Client name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input type="text" value={editForm.customer_company} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_company: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Company name" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input type="text" value={editForm.customer_address} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Billing address" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                  <input type="text" value={editForm.customer_gstin} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_gstin: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="GSTIN" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                  <input type="text" value={editForm.customer_pan} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_pan: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="PAN" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={editForm.invoice_date}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    value={editForm.invoice_number}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, invoice_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.subtotal}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, subtotal: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.tax_amount}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, tax_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.total_amount}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, total_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Services</label>
                <div className="space-y-3">
                  {editForm.items.map((item, idx) => (
                    <div key={item.id || idx} className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto_auto_auto] gap-2 border border-gray-200 rounded-md p-3 items-end">
                      <input
                        type="text"
                        value={item.sac_code || item.hsn_code || ''}
                        onChange={(e) => handleEditItemChange(idx, 'sac_code', e.target.value)}
                        placeholder="SAC / HSN Code"
                        className="px-2 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleEditItemChange(idx, 'description', e.target.value)}
                        placeholder="Description of Goods / Services"
                        className="px-2 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <input
                        type="text"
                        value={item.quantity}
                        onChange={(e) => handleEditItemChange(idx, 'quantity', e.target.value)}
                        placeholder="Qty"
                        className="px-2 py-2 border border-gray-300 rounded-md text-sm w-full min-w-[4rem]"
                      />
                      <input
                        type="text"
                        value={item.amount}
                        onChange={(e) => handleEditItemChange(idx, 'amount', e.target.value)}
                        placeholder="Amount (₹)"
                        className="px-2 py-2 border border-gray-300 rounded-md text-sm w-full min-w-[5rem]"
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => handleRemoveEditItem(idx)} className="shrink-0">
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={handleAddEditItem} className="inline-flex items-center space-x-2">
                    <Plus className="w-4 h-4" />
                    <span>Add Item</span>
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Update notes" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
                  <textarea rows={3} value={editForm.terms} onChange={(e) => setEditForm((prev) => ({ ...prev, terms: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Update terms" />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); setEditBill(null); }}>Cancel</Button>
                <Button type="submit" disabled={isEditLoading}>{isEditLoading ? 'Updating...' : 'Update Bill of Supply'}</Button>
              </div>
            </form>
        </DocumentModalShell>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Bill of Supply</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete{' '}
                <span className="font-semibold">{deleteTarget.invoice_number || `#${deleteTarget.id}`}</span>?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setIsDeleteOpen(false); setDeleteTarget(null); }} disabled={isDeleteLoading}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-[100]">
          <div className="bg-green-600 text-white px-4 py-3 rounded-md shadow-lg flex items-center space-x-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">{successMessage}</span>
            <button onClick={() => setSuccessMessage('')} className="text-white/80 hover:text-white text-xs">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillsOfSupply;

